import { CalendarDays, ChevronRight, Loader2, Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { PaneHeader } from '@/components/settings/pane-header';
import {
  CalendarKindChip,
  CalendarStatusChip,
  LocalOnlyBadge,
  NonBlockingBadge,
  PlatoOriginBadge,
} from '@/components/settings/calendars/calendar-badges';
import {
  calendarKind,
  calendarKindLabel,
  groupCalendarsForSettings,
  isPlatoCalendar,
  lookupOptionLabel,
  prettyTimeZone,
  usageSummary,
} from '@/lib/calendars';
import { cn } from '@/lib/utils';

const PLATO_DELETE_TITLE = 'Managed by Plato — cannot be deleted here';

function CalendarCard({ calendar, canEdit, doctorOptions, clinicOptions, onEdit, onDelete, deleting }) {
  const plato = isPlatoCalendar(calendar);
  const kind = calendarKind(calendar);
  const doctorName = lookupOptionLabel(doctorOptions, calendar.doctorId);
  const clinicName = lookupOptionLabel(clinicOptions, calendar.tenantId);
  // A Plato doctor calendar's tenant is a conversion row ("Healo Technical", "OT"…) — meaningless
  // to the reader, so the clinic chip is only shown on local doctor calendars.
  const showClinic = kind === 'doctor' && Boolean(calendar.tenantId) && !plato;

  return (
    <div className="flex items-center gap-4 rounded-xl bg-card p-4 shadow-[inset_0_0_0_1px_var(--border)]">
      <span
        className="w-1 self-stretch rounded-full"
        style={{ backgroundColor: calendar.colorHex || 'var(--border)' }}
        aria-hidden
      />
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="truncate text-base font-extrabold text-foreground">{calendar.name || 'Untitled calendar'}</span>
          <CalendarStatusChip active={calendar.status === 'active'} />
          {plato ? <PlatoOriginBadge /> : null}
          {calendar.nonBlocking && kind === 'doctor' ? <NonBlockingBadge /> : null}
          {calendar.localOnly ? <LocalOnlyBadge /> : null}
        </div>
        <div className="mt-2.5 flex flex-wrap items-center gap-2">
          <CalendarKindChip kind={kind} label={calendarKindLabel(calendar, { doctorName, clinicName })} />
          {showClinic ? <CalendarKindChip kind="clinic" label={clinicName || 'Clinic'} /> : null}
          <span className="text-[12px] font-semibold text-muted-foreground">{usageSummary(calendar.usage)}</span>
          {calendar.timezone ? (
            <span className="text-[12px] text-muted-foreground">· {prettyTimeZone(calendar.timezone)}</span>
          ) : null}
        </div>
      </div>
      <div className="flex shrink-0 items-center gap-1.5">
        {canEdit ? (
          <button
            type="button"
            onClick={() => onDelete(calendar)}
            disabled={plato || deleting}
            aria-label={`Delete calendar ${calendar.name}`}
            title={plato ? PLATO_DELETE_TITLE : 'Delete calendar'}
            className={cn(
              'inline-flex h-9 w-9 items-center justify-center rounded-md border border-transparent text-muted-foreground transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
              plato || deleting
                ? 'cursor-not-allowed opacity-40'
                : 'hover:border-destructive/40 hover:bg-destructive/10 hover:text-destructive',
            )}
          >
            {deleting ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : <Trash2 className="h-4 w-4" aria-hidden />}
          </button>
        ) : null}
        <Button type="button" variant="outline" size="sm" onClick={() => onEdit(calendar.id)}>
          {canEdit && !plato ? 'Edit' : 'Open'}
          <ChevronRight className="ml-1 h-3.5 w-3.5" aria-hidden />
        </Button>
      </div>
    </div>
  );
}

function Section({ label, count, children }) {
  return (
    <div className="flex flex-col gap-3">
      <div className="mt-1 flex items-center gap-3 first:mt-0">
        <span className="text-[11px] font-extrabold uppercase tracking-wide text-muted-foreground">{label}</span>
        <span className="h-px flex-1 bg-border" />
        <span className="text-[11px] font-semibold text-muted-foreground">{count}</span>
      </div>
      {children}
    </div>
  );
}

const SECTIONS = [
  { key: 'doctor', label: 'Doctor calendars', empty: 'No doctor calendars.' },
  { key: 'clinic', label: 'Clinic calendars', empty: 'No clinic calendars.' },
  { key: 'standalone', label: 'Standalone calendars', empty: 'No standalone calendars.' },
];

/** Pane 1: every calendar in three sections, with create / edit / delete. */
export function CalendarsListPane({
  calendars,
  loading,
  canEdit,
  doctorOptions = [],
  clinicOptions = [],
  deletingId = '',
  onCreate,
  onEdit,
  onDelete,
}) {
  const groups = groupCalendarsForSettings(calendars, { doctorOptions, clinicOptions });
  const total = calendars.length;

  return (
    <div className="flex h-full min-h-0 flex-col">
      <PaneHeader
        title="Calendars"
        description="Blocking calendars share their doctor's busy pool, non-blocking ones run independently of the doctor's other calendars, and standalone calendars (no doctor, no clinic) are filled directly in the Calendar page."
        action={
          canEdit ? (
            <Button type="button" size="default" onClick={onCreate}>
              <Plus className="mr-1.5 h-4 w-4" aria-hidden />
              New calendar
            </Button>
          ) : null
        }
      />
      <ScrollArea className="min-h-0 flex-1" viewportClassName="p-6">
        <div className="mx-auto flex max-w-4xl flex-col gap-3">
          {loading ? (
            <div className="flex items-center justify-center gap-2 py-16 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
              Loading calendars…
            </div>
          ) : (
            <>
              {SECTIONS.map((section) => {
                const items = groups[section.key];
                if (items.length === 0 && total > 0) return null;
                return (
                  <Section key={section.key} label={section.label} count={items.length}>
                    {items.length === 0 ? (
                      <div className="rounded-md border border-dashed border-border px-3 py-6 text-center text-sm text-muted-foreground">
                        {section.empty}
                      </div>
                    ) : (
                      items.map((calendar) => (
                        <CalendarCard
                          key={calendar.id}
                          calendar={calendar}
                          canEdit={canEdit}
                          doctorOptions={doctorOptions}
                          clinicOptions={clinicOptions}
                          onEdit={onEdit}
                          onDelete={onDelete}
                          deleting={deletingId === calendar.id}
                        />
                      ))
                    )}
                  </Section>
                );
              })}
              {canEdit ? (
                <button
                  type="button"
                  onClick={onCreate}
                  className="flex items-center justify-center gap-2 rounded-xl border border-dashed border-border px-5 py-5 text-[13.5px] font-bold text-muted-foreground transition hover:bg-accent/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <CalendarDays className="h-4 w-4" aria-hidden />
                  Create a new calendar
                </button>
              ) : null}
            </>
          )}
        </div>
      </ScrollArea>
    </div>
  );
}
